(() => {
  if (window.aknesProductFaqRuntimeReady) return;
  window.aknesProductFaqRuntimeReady = true;

  const documentationControllers = new WeakMap();
  const searchControllers = new WeakMap();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const NAV_CLICK_LOCK_MS = 600;

  const normalize = (value) => String(value || '').trim().toLocaleLowerCase();
  const handleize = (value) => normalize(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\u2018\u2019']/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const getDocumentation = () => document.querySelector('[data-aknes-product-faq-documentation]');

  const resolveFaqTarget = (documentation, rawKey) => {
    if (!documentation) return null;

    const exactKey = String(rawKey || '').trim().replace(/^#+/, '');
    if (!exactKey) return null;

    const exactTarget = document.getElementById(exactKey);
    if (exactTarget && documentation.contains(exactTarget)) return exactTarget;

    const normalizedKey = handleize(exactKey);
    if (!normalizedKey) return null;

    const anchorTarget = Array.from(documentation.querySelectorAll('[data-aknes-faq-anchor-key]'))
      .find((node) => handleize(node.dataset.aknesFaqAnchorKey) === normalizedKey);
    if (anchorTarget) return anchorTarget;

    return Array.from(documentation.querySelectorAll('[data-aknes-faq-nav-label]'))
      .find((node) => handleize(node.dataset.aknesFaqNavLabel) === normalizedKey) || null;
  };

  const revealFaqTarget = (documentation, target) => {
    if (!documentation || !target) return;

    let current = target;
    while (current && current !== documentation) {
      if (current.matches?.('details') && !current.open) current.open = true;
      current = current.parentElement;
    }
  };

  const setNavigationGroupExpanded = (groupNode, expanded) => {
    if (!groupNode || groupNode.dataset.aknesFaqNavCollapsible !== 'true') return;

    const toggle = groupNode.querySelector(':scope > .aknes-faq-sidebar-group__header > [data-aknes-faq-nav-toggle]');
    const children = groupNode.querySelector(':scope > .aknes-faq-sidebar-group__children');
    const label = toggle?.dataset.aknesFaqNavToggleLabel || 'navigation';
    groupNode.dataset.expanded = String(expanded);
    groupNode.classList.toggle('is-collapsed', !expanded);
    children?.setAttribute('aria-hidden', String(!expanded));
    toggle?.setAttribute('aria-expanded', String(expanded));
    toggle?.setAttribute('aria-label', `${expanded ? 'Collapse' : 'Expand'} ${label} navigation`);
  };

  const expandNavigationGroup = (documentation, groupId) => {
    if (!documentation || !groupId) return;
    documentation.querySelectorAll('[data-aknes-faq-toc-group]').forEach((groupNode) => {
      if (groupNode.dataset.aknesFaqTocGroup === groupId) setNavigationGroupExpanded(groupNode, true);
    });
  };

  const syncNavigationVisibility = (documentation) => {
    documentation.querySelectorAll('[data-aknes-faq-nav-target]').forEach((link) => {
      const target = document.getElementById(link.dataset.aknesFaqNavTarget || '');
      const hidden = !target || target.hidden || Boolean(target.closest('[data-aknes-faq-nav-group][hidden]'));
      const item = link.closest('li');
      link.hidden = hidden;
      if (item) item.hidden = hidden;
    });
  };

  const filterDocumentation = (documentation, rawTerm) => {
    if (!documentation) return { visibleCount: 0, firstVisibleItem: null };

    const term = normalize(rawTerm);
    const items = Array.from(documentation.querySelectorAll('[data-aknes-faq-search-item]'));
    const groups = Array.from(documentation.querySelectorAll('[data-aknes-product-faq-group]'));
    const emptyState = documentation.querySelector('[data-aknes-product-faq-no-results]');
    let visibleCount = 0;
    let firstVisibleItem = null;

    items.forEach((item) => {
      const searchableText = `${item.dataset.searchText || ''} ${item.textContent || ''}`;
      const matches = !term || normalize(searchableText).includes(term);
      item.hidden = !matches;
      if (matches) visibleCount += 1;
    });

    groups.forEach((group) => {
      const groupItems = Array.from(group.querySelectorAll('[data-aknes-faq-search-item]'));
      group.hidden = Boolean(term) && (groupItems.length === 0 || groupItems.every((item) => item.hidden));
      if (term && !group.hidden) expandNavigationGroup(documentation, group.id);
    });

    if (emptyState) emptyState.hidden = !term || visibleCount > 0;
    syncNavigationVisibility(documentation);

    if (term && visibleCount > 0) {
      firstVisibleItem = items.find((item) => !item.hidden && !item.closest('[data-aknes-faq-nav-group][hidden]')) || null;
      const parentGroup = firstVisibleItem?.closest('[data-aknes-faq-nav-group]');
      const groupId = firstVisibleItem?.dataset.aknesFaqGroupId || parentGroup?.dataset.aknesProductFaqGroupId || parentGroup?.id || '';
      const itemId = firstVisibleItem?.hasAttribute('data-aknes-faq-nav-item') ? firstVisibleItem.id : '';
      if (groupId) setActiveNavigation(documentation, groupId, itemId);
    }

    document.querySelectorAll('[data-aknes-product-faq-result-count]').forEach((node) => {
      node.hidden = !term;
      node.textContent = term ? `${visibleCount} ${visibleCount === 1 ? 'result' : 'results'}` : '';
    });

    return { visibleCount, firstVisibleItem };
  };

  const syncSearchInputs = (value, source) => {
    document.querySelectorAll('[data-aknes-product-faq-search-input]').forEach((input) => {
      if (input !== source) input.value = value;
    });
  };

  const runSearch = (value, source) => {
    const documentation = getDocumentation();
    syncSearchInputs(value, source);
    return filterDocumentation(documentation, value);
  };

  const bindSearchRoot = (root) => {
    if (!root || searchControllers.has(root)) return;

    const controller = new AbortController();
    const { signal } = controller;
    const input = root.querySelector('[data-aknes-product-faq-search-input]');
    const button = root.querySelector('[data-aknes-product-faq-search-button]');

    if (input) {
      input.addEventListener('input', () => runSearch(input.value, input), { signal });
      input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        runSearch(input.value, input);
      }, { signal });
    }

    if (button && input) button.addEventListener('click', () => runSearch(input.value, input), { signal });

    root.querySelectorAll('[data-aknes-faq-search-suggestion], [data-aknes-product-faq-search-term]').forEach((suggestion) => {
      suggestion.addEventListener('click', () => {
        const term = suggestion.dataset.searchTerm || suggestion.dataset.aknesProductFaqSearchTerm || '';
        if (input) input.value = term;
        const result = runSearch(term, input);
        if (window.matchMedia('(max-width: 989px)').matches && result?.firstVisibleItem) {
          result.firstVisibleItem.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
        }
      }, { signal });
    });

    root.querySelectorAll('[data-aknes-faq-jump-suggestion]').forEach((suggestion) => {
      suggestion.addEventListener('click', (event) => {
        const documentation = getDocumentation();
        const targetKey = suggestion.dataset.aknesFaqTargetKey || suggestion.getAttribute('href') || '';
        const target = resolveFaqTarget(documentation, targetKey);
        if (!target) return;

        event.preventDefault();
        if (input) input.value = '';
        runSearch('', input);
        revealFaqTarget(documentation, target);
        documentation.dispatchEvent(new CustomEvent('aknes:faq:activate-target', {
          detail: { targetId: target.id },
        }));

        const targetHash = `#${encodeURIComponent(target.id)}`;
        if (window.location.hash !== targetHash) window.history.pushState(null, '', targetHash);
        target.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
      }, { signal });
    });

    searchControllers.set(root, controller);
  };

  const keepNavigationLinkVisible = (link) => {
    const mobileNav = link.closest('.aknes-product-faq-documentation__mobile-nav');
    if (mobileNav) {
      const linkRect = link.getBoundingClientRect();
      const navRect = mobileNav.getBoundingClientRect();
      if (linkRect.left < navRect.left) mobileNav.scrollLeft -= navRect.left - linkRect.left;
      else if (linkRect.right > navRect.right) mobileNav.scrollLeft += linkRect.right - navRect.right;
      return;
    }

    const sidebar = link.closest('.aknes-product-faq-sidebar');
    if (!sidebar) return;
    const linkRect = link.getBoundingClientRect();
    const sidebarRect = sidebar.getBoundingClientRect();
    if (linkRect.top < sidebarRect.top) sidebar.scrollTop -= sidebarRect.top - linkRect.top;
    else if (linkRect.bottom > sidebarRect.bottom) sidebar.scrollTop += linkRect.bottom - sidebarRect.bottom;
  };

  const setActiveNavigation = (documentation, groupId, questionId = '') => {
    const activeTarget = questionId || groupId;
    const activeChanged = documentation.dataset.aknesFaqActiveTarget !== activeTarget;
    documentation.dataset.aknesFaqActiveTarget = activeTarget;

    documentation.querySelectorAll('[data-aknes-product-faq-nav-link]').forEach((link) => {
      const active = link.dataset.aknesProductFaqNavLink === groupId;
      link.classList.toggle('is-active', active);
      if (active) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');

      if (active) keepNavigationLinkVisible(link);
    });

    documentation.querySelectorAll('[data-aknes-faq-toc-group]').forEach((groupNode) => {
      groupNode.classList.toggle('is-active', groupNode.dataset.aknesFaqTocGroup === groupId);
    });

    documentation.querySelectorAll('[data-aknes-faq-question-nav]').forEach((link) => {
      const active = Boolean(questionId) && link.dataset.aknesFaqQuestionNav === questionId;
      link.classList.toggle('is-active', active);
      if (active) {
        link.setAttribute('aria-current', 'location');
        keepNavigationLinkVisible(link);
      } else {
        link.removeAttribute('aria-current');
      }
    });

    if (questionId || activeChanged) expandNavigationGroup(documentation, groupId);
  };

  const createGroupLink = (model, className = '') => {
    const link = document.createElement('a');
    link.href = `#${model.id}`;
    link.textContent = model.label;
    link.dataset.aknesProductFaqNavLink = model.id;
    link.dataset.aknesFaqNavTarget = model.id;
    if (className) link.className = className;
    return link;
  };

  const createItemLink = (model, itemModel) => {
    const link = document.createElement('a');
    link.href = `#${itemModel.id}`;
    link.textContent = itemModel.label;
    link.dataset.aknesFaqQuestionNav = itemModel.id;
    link.dataset.aknesFaqQuestionGroup = model.id;
    link.dataset.aknesFaqNavTarget = itemModel.id;
    return link;
  };

  const createNavigationToggle = (model, controlsId, expanded) => {
    const button = document.createElement('button');
    button.className = 'aknes-faq-sidebar-group__toggle';
    button.type = 'button';
    button.dataset.aknesFaqNavToggle = '';
    button.dataset.aknesFaqNavToggleLabel = model.label;
    button.setAttribute('aria-controls', controlsId);
    button.setAttribute('aria-expanded', String(expanded));
    button.setAttribute('aria-label', `${expanded ? 'Collapse' : 'Expand'} ${model.label} navigation`);

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'm7 10 5 5 5-5');
    svg.append(path);
    button.append(svg);
    return button;
  };

  const buildNestedContents = (target, models, options = {}) => {
    if (!target) return;
    target.replaceChildren();

    const { sidebar = false, mobileDefaultCollapsed = false } = options;
    const list = document.createElement('ul');
    models.forEach((model) => {
      const groupItem = document.createElement('li');
      groupItem.className = 'aknes-faq-sidebar-group';
      groupItem.dataset.aknesFaqTocGroup = model.id;
      const groupHeader = document.createElement('div');
      groupHeader.className = 'aknes-faq-sidebar-group__header';
      groupHeader.append(createGroupLink(model, sidebar ? 'aknes-product-faq-sidebar__group-link' : ''));

      if (model.items.length) {
        const childrenId = `${documentationId(target)}-${sidebar ? 'desktop' : 'mobile'}-${model.id}-children`;
        const collapsible = model.collapsible;
        const expanded = collapsible ? (sidebar ? model.defaultExpanded : !mobileDefaultCollapsed && model.defaultExpanded) : true;
        if (collapsible) groupHeader.append(createNavigationToggle(model, childrenId, expanded));

        const childRegion = document.createElement('div');
        childRegion.id = childrenId;
        childRegion.className = 'aknes-faq-sidebar-group__children';
        childRegion.setAttribute('aria-hidden', String(!expanded));
        const childList = document.createElement('ul');
        childList.className = 'aknes-product-faq-sidebar__questions';
        childList.dataset.aknesFaqQuestionList = '';
        childList.classList.add('aknes-faq-sidebar-group__children-inner');
        model.items.forEach((itemModel) => {
          const childItem = document.createElement('li');
          childItem.append(createItemLink(model, itemModel));
          childList.append(childItem);
        });
        childRegion.append(childList);
        groupItem.append(groupHeader, childRegion);
        groupItem.dataset.aknesFaqNavCollapsible = String(collapsible);
        groupItem.dataset.expanded = String(expanded);
        groupItem.classList.toggle('is-collapsible', collapsible);
        groupItem.classList.toggle('is-collapsed', collapsible && !expanded);
      } else {
        groupItem.append(groupHeader);
        groupItem.dataset.aknesFaqNavCollapsible = 'false';
        groupItem.dataset.expanded = 'true';
      }

      list.append(groupItem);
    });
    target.append(list);
  };

  const documentationId = (target) => {
    const documentation = target.closest('[data-aknes-product-faq-documentation]');
    return documentation?.id || 'aknes-faq-documentation';
  };

  const buildNavigation = (documentation) => {
    const groups = Array.from(documentation.querySelectorAll('[data-aknes-faq-nav-group]'));
    const models = groups.reduce((result, group) => {
      const label = (group.dataset.aknesFaqNavLabel || '').trim();
      const show = group.dataset.aknesFaqShowInNavigation !== 'false';
      if (!show || !group.id || !label) return result;

      group.dataset.aknesProductFaqGroupId = group.id;
      const items = Array.from(group.querySelectorAll('[data-aknes-faq-nav-item]'))
        .filter((item) => item.closest('[data-aknes-faq-nav-group]') === group)
        .reduce((itemResult, item) => {
          const itemLabel = (item.dataset.aknesFaqNavLabel || '').trim();
          const itemShow = item.dataset.aknesFaqShowInNavigation !== 'false';
          item.dataset.aknesFaqGroupId = group.id;
          item.dataset.aknesFaqParent = group.id;
          if (itemShow && item.id && itemLabel) itemResult.push({ id: item.id, label: itemLabel, node: item });
          return itemResult;
        }, []);

      result.push({
        id: group.id,
        label,
        node: group,
        items,
        collapsible: group.dataset.aknesSidebarCollapsible !== 'false',
        defaultExpanded: group.dataset.aknesSidebarExpanded !== 'false',
      });
      return result;
    }, []);

    const sidebarContents = documentation.querySelector('[data-aknes-faq-sidebar-contents]');
    const sidebarNav = documentation.querySelector('[data-aknes-faq-sidebar-nav]');
    const mobileContents = documentation.querySelector('[data-aknes-faq-mobile-contents]');
    const mobileToc = documentation.querySelector('[data-aknes-faq-mobile-toc]');
    const mobileGroupNav = documentation.querySelector('[data-aknes-faq-mobile-group-nav]');
    const emptyNote = documentation.querySelector('[data-aknes-faq-nav-empty]');
    const designMode = documentation.dataset.aknesFaqDesignMode === 'true';

    const mobileDefaultCollapsed = documentation.dataset.aknesFaqMobileGroupsCollapsed !== 'false';
    buildNestedContents(sidebarNav, models, { sidebar: true });
    buildNestedContents(mobileToc, models, { mobileDefaultCollapsed });

    if (mobileGroupNav) {
      mobileGroupNav.replaceChildren();
      models.forEach((model) => mobileGroupNav.append(createGroupLink(model)));
    }

    const hasNavigation = models.length > 0;
    if (sidebarContents) sidebarContents.hidden = !hasNavigation && !designMode;
    if (emptyNote) emptyNote.hidden = hasNavigation || !designMode;
    if (sidebarNav) sidebarNav.hidden = !hasNavigation;
    if (mobileContents) mobileContents.hidden = !hasNavigation;
    if (mobileGroupNav) mobileGroupNav.hidden = !hasNavigation;

    return {
      groups: models.map((model) => model.node),
      models,
      items: models.flatMap((model) => model.items.map((item) => item.node)),
    };
  };

  const hydrateResourceCards = (documentation) => {
    const sources = Array.from(documentation.querySelectorAll('[data-aknes-faq-resource-source]'));
    const desktopTarget = documentation.querySelector('[data-aknes-faq-sidebar-resource-target]');
    const mobileTarget = documentation.querySelector('[data-aknes-faq-mobile-resource-target]');

    const cloneCard = (source) => {
      const card = source.querySelector('.aknes-product-faq-sidebar-resource')?.cloneNode(true);
      if (!card) return null;
      card.querySelectorAll('[data-shopify-editor-block]').forEach((node) => node.removeAttribute('data-shopify-editor-block'));
      return card;
    };

    sources.forEach((source) => {
      const desktopCard = cloneCard(source);
      const mobileCard = cloneCard(source);
      if (desktopCard) desktopTarget?.append(desktopCard);
      if (mobileCard) mobileTarget?.append(mobileCard);
    });

    const desktopResources = documentation.querySelector('[data-aknes-faq-sidebar-resources]');
    const mobileResources = documentation.querySelector('[data-aknes-faq-mobile-resources]');
    const desktopHasResources = Boolean(desktopTarget?.children.length || desktopResources?.querySelector('.aknes-product-faq-manual-quick-link'));
    const mobileHasResources = Boolean(mobileTarget?.children.length || mobileResources?.querySelector('.aknes-product-faq-manual-quick-link'));
    if (desktopResources) desktopResources.hidden = !desktopHasResources;
    if (mobileResources) mobileResources.hidden = !mobileHasResources;

    const sidebar = documentation.querySelector('.aknes-product-faq-sidebar');
    const sidebarContents = documentation.querySelector('[data-aknes-faq-sidebar-contents]');
    const hideSidebar = Boolean(sidebarContents?.hidden && desktopResources?.hidden);
    if (sidebar) sidebar.hidden = hideSidebar;
    documentation.classList.toggle('aknes-product-faq-documentation--no-sidebar', hideSidebar);
  };

  const bindLightbox = (documentation, signal) => {
    const dialog = documentation.querySelector('[data-aknes-faq-lightbox]');
    const image = dialog?.querySelector('[data-aknes-faq-lightbox-image]');
    const caption = dialog?.querySelector('[data-aknes-faq-lightbox-caption]');
    const closeButton = dialog?.querySelector('[data-aknes-faq-lightbox-close]');
    let returnFocus = null;

    if (!dialog || !image || !caption) return;

    const clearDialog = () => {
      image.src = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';
      image.alt = '';
      caption.textContent = '';
      caption.hidden = true;
      const focusTarget = returnFocus;
      returnFocus = null;
      focusTarget?.focus({ preventScroll: true });
    };

    const closeDialog = () => {
      if (dialog.open && typeof dialog.close === 'function') dialog.close();
      else {
        dialog.removeAttribute('open');
        clearDialog();
      }
    };

    documentation.querySelectorAll('[data-aknes-faq-lightbox-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const fullImage = trigger.dataset.fullImage;
        if (!fullImage) return;
        returnFocus = trigger;
        image.src = fullImage;
        image.alt = trigger.dataset.imageAlt || '';
        caption.textContent = trigger.dataset.imageCaption || '';
        caption.hidden = !caption.textContent;
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        closeButton?.focus({ preventScroll: true });
      }, { signal });
    });

    closeButton?.addEventListener('click', closeDialog, { signal });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    }, { signal });
    dialog.addEventListener('close', clearDialog, { signal });
  };

  const bindDocumentation = (documentation) => {
    if (!documentation || documentationControllers.has(documentation)) return;

    const controller = new AbortController();
    const { signal } = controller;
    const navigation = buildNavigation(documentation);
    const groups = navigation.groups;
    const navigationItems = navigation.items;
    const observedTargets = [...groups, ...navigationItems];
    const observerNodes = observedTargets.map((target) => (
      target.querySelector(':scope > summary, :scope > .aknes-product-faq-group__header, :scope > h3') || target
    ));
    let observer = null;
    let manualNavigationTarget = '';
    let manualNavigationUntil = 0;

    const getReadingLine = () => {
      const variableName = window.matchMedia('(max-width: 989px)').matches
        ? '--aknes-faq-mobile-anchor-offset'
        : '--aknes-faq-anchor-offset';
      const configuredOffset = Number.parseFloat(getComputedStyle(documentation).getPropertyValue(variableName)) || 0;
      return Math.min(Math.max(configuredOffset, 0), window.innerHeight * 0.3);
    };

    const applyNavigationTarget = (targetId) => {
      if (!targetId) return false;
      const target = document.getElementById(targetId);
      if (!target || !documentation.contains(target)) return false;

      if (target.hasAttribute('data-aknes-faq-nav-item')) {
        const groupId = target.dataset.aknesFaqGroupId || target.dataset.aknesFaqParent || '';
        if (!groupId) return false;
        setActiveNavigation(documentation, groupId, target.id);
        return true;
      }

      if (target.hasAttribute('data-aknes-faq-nav-group')) {
        const groupId = target.dataset.aknesProductFaqGroupId || target.id;
        setActiveNavigation(documentation, groupId);
        return true;
      }

      return false;
    };

    const selectReadingTarget = () => {
      const readingLine = getReadingLine();
      const candidates = observedTargets
        .filter((target) => !target.hidden && !target.closest('[data-aknes-faq-nav-group][hidden]'))
        .map((target) => ({ target, top: target.getBoundingClientRect().top }));
      if (!candidates.length) return null;

      const passed = candidates.filter((candidate) => candidate.top <= readingLine + 1);
      if (passed.length) return passed.sort((a, b) => b.top - a.top)[0].target;
      return candidates.sort((a, b) => Math.abs(a.top - readingLine) - Math.abs(b.top - readingLine))[0].target;
    };

    const activateManualNavigation = (targetId) => {
      manualNavigationTarget = targetId;
      manualNavigationUntil = performance.now() + NAV_CLICK_LOCK_MS;
      applyNavigationTarget(targetId);
    };

    documentation.addEventListener('aknes:faq:activate-target', (event) => {
      activateManualNavigation(event.detail?.targetId || '');
    }, { signal });

    const getHashTarget = () => {
      if (!window.location.hash) return '';
      try {
        return decodeURIComponent(window.location.hash.slice(1));
      } catch (_error) {
        return window.location.hash.slice(1);
      }
    };

    hydrateResourceCards(documentation);
    bindLightbox(documentation, signal);

    documentation.addEventListener('contextmenu', (event) => {
      if (event.target.closest('[data-aknes-faq-protected-video]')) event.preventDefault();
    }, { signal });

    if ('IntersectionObserver' in window && groups.length) {
      const observerReadingLine = Math.round(getReadingLine());
      const observerBottomMargin = Math.max(0, window.innerHeight - observerReadingLine - 2);
      const observerRootMargin = `-${observerReadingLine}px 0px -${observerBottomMargin}px 0px`;
      documentation.dataset.aknesFaqObserverRootMargin = observerRootMargin;
      observer = new IntersectionObserver(() => {
        if (performance.now() < manualNavigationUntil) {
          applyNavigationTarget(manualNavigationTarget);
          return;
        }

        const target = selectReadingTarget();
        if (target) applyNavigationTarget(target.id);
      }, { rootMargin: observerRootMargin, threshold: [0, 0.04, 0.15] });

      observerNodes.forEach((node) => observer.observe(node));
    }

    const initialHashTarget = getHashTarget();
    const initialTargetApplied = applyNavigationTarget(initialHashTarget);
    if (!initialTargetApplied && groups[0]) setActiveNavigation(documentation, groups[0].dataset.aknesProductFaqGroupId);

    window.addEventListener('hashchange', () => applyNavigationTarget(getHashTarget()), { signal });

    documentation.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-aknes-faq-nav-toggle]');
      if (toggle && documentation.contains(toggle)) {
        const groupNode = toggle.closest('[data-aknes-faq-toc-group]');
        setNavigationGroupExpanded(groupNode, toggle.getAttribute('aria-expanded') !== 'true');
        return;
      }

      const groupLink = event.target.closest('[data-aknes-product-faq-nav-link]');
      if (groupLink && documentation.contains(groupLink)) {
        activateManualNavigation(groupLink.dataset.aknesProductFaqNavLink);
        groupLink.closest('.aknes-product-faq-mobile-contents')?.removeAttribute('open');
        return;
      }

      const itemLink = event.target.closest('[data-aknes-faq-question-nav]');
      if (itemLink && documentation.contains(itemLink)) {
        activateManualNavigation(itemLink.dataset.aknesFaqQuestionNav);
        itemLink.closest('.aknes-product-faq-mobile-contents')?.removeAttribute('open');
      }
    }, { signal });

    documentation.querySelectorAll('[data-aknes-product-faq-clear-search]').forEach((button) => {
      button.addEventListener('click', () => runSearch('', null), { signal });
    });

    documentationControllers.set(documentation, { controller, observer });
  };

  const init = (scope = document) => {
    scope.querySelectorAll?.('[data-aknes-product-faq-hero]').forEach(bindSearchRoot);
    scope.querySelectorAll?.('[data-aknes-product-faq-documentation]').forEach(bindDocumentation);
    if (scope.matches?.('[data-aknes-product-faq-hero]')) bindSearchRoot(scope);
    if (scope.matches?.('[data-aknes-product-faq-documentation]')) bindDocumentation(scope);
  };

  const destroy = (scope) => {
    const roots = [scope, ...Array.from(scope.querySelectorAll?.('[data-aknes-product-faq-hero], [data-aknes-product-faq-documentation]') || [])];
    roots.forEach((root) => {
      const searchController = searchControllers.get(root);
      if (searchController) {
        searchController.abort();
        searchControllers.delete(root);
      }
      const record = documentationControllers.get(root);
      if (record) {
        record.controller.abort();
        record.observer?.disconnect();
        documentationControllers.delete(root);
      }
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
